import { useState, useEffect } from 'react';
import { apiClient } from '@services/api';
import Icon from '@material-ui/core/Icon';
import CircularProgress from '@material-ui/core/CircularProgress';
import TextField from '@components/TextField';
import SelectSearch from 'react-select';
import NumberFormat from 'react-number-format';
import Moment from 'react-moment';
import { useHistory } from 'react-router-dom';
import { darkSelectStyles } from '@utils/darkSelectStyles';
import useIsNarrow from '@utils/useIsNarrow';

const URGENCY_BANDS = [
  { key: 'critical', label: 'Crítico',         days: 3,        color: '#ef4444', bg: 'rgba(239,68,68,0.1)',    icon: 'priority_high',   desc: 'Falta 3 dias ou menos' },
  { key: 'urgent',   label: 'Urgente',          days: 7,        color: '#f97316', bg: 'rgba(249,115,22,0.1)',   icon: 'warning',         desc: 'Falta até 7 dias' },
  { key: 'warning',  label: 'Atenção',          days: 30,       color: '#eab308', bg: 'rgba(234,179,8,0.1)',    icon: 'schedule',        desc: 'Falta até 30 dias' },
  { key: 'normal',   label: 'Normal',           days: 60,       color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',   icon: 'event',           desc: 'Falta até 60 dias' },
  { key: 'low',      label: 'Baixa prioridade', days: Infinity, color: '#94a3b8', bg: 'rgba(148,163,184,0.1)', icon: 'hourglass_empty', desc: 'Mais de 60 dias' },
];

const CONCLUDED_BAND = { key: 'concluded', label: 'Alerta', color: '#f59e0b', bg: 'rgba(245,158,11,0.1)', icon: 'notification_important', desc: '1–30 dias pós último serviço com valor pendente' };
const LOST_BAND      = { key: 'lost',      label: 'Perdidos',   color: '#dc2626', bg: 'rgba(220,38,38,0.1)',   icon: 'money_off', desc: '31+ dias sem pagamento após serviço' };

const getBand = (days) => {
  if (days === null || days === undefined) return URGENCY_BANDS[4];
  if (days <= 3)  return URGENCY_BANDS[0];
  if (days <= 7)  return URGENCY_BANDS[1];
  if (days <= 30) return URGENCY_BANDS[2];
  if (days <= 60) return URGENCY_BANDS[3];
  return URGENCY_BANDS[4];
};

// Retorna dias desde o último serviço (inteiro), ou null se não houver
const daysSinceLast = (row) => {
  if (!row.last_service_date) return null;
  return Math.floor((new Date() - new Date(row.last_service_date)) / (1000 * 60 * 60 * 24));
};

// Classifica rows sem serviço futuro por dias desde último serviço
// Retorna: 'nodate' | 'concluded' (1–30d) | 'lost' (31+d)
const classifyPostService = (row) => {
  const d = daysSinceLast(row);
  if (d === null || d < 1)  return 'nodate';
  if (d >= 31)               return 'lost';
  return 'concluded'; // 1–30 dias
};

const currency = (val) => (
  <NumberFormat
    value={Number(val) || 0}
    displayType="text"
    decimalSeparator=","
    decimalScale={2}
    fixedDecimalScale
    thousandSeparator="."
    prefix={`${process.env.REACT_APP_CURRENCY} `}
    renderText={(v) => <span>{v}</span>}
  />
);

const fmtDate = (d) => d ? <Moment format="DD/MM/YY" date={d} /> : <span style={{ color: '#475569' }}>—</span>;

// Soma um campo numérico das linhas (ex: 'remaining' = projetado a receber)
const sumField = (rows, field) => rows.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);

// Rodapé com somatória do bloco — alinhado às colunas (Total / Pago / Restante)
const TableFoot = ({ rows, color }) => {
  const narrow = useIsNarrow();
  // Mobile/tablet: some a grade de 9 colunas e mostra só os 3 números que importam
  if (narrow) {
    return (
      <div style={{
        display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10,
        padding: '10px 14px', background: 'rgba(255,255,255,0.05)', borderTop: '1px solid rgba(255,255,255,0.1)',
      }}>
        {[['Total', sumField(rows, 'total'), '#e8f0fb'],
          ['Pago', sumField(rows, 'customer_paid_amount'), '#4ade80'],
          ['Restante', sumField(rows, 'remaining'), color || '#f87171']].map(([lbl, val, col]) => (
          <div key={lbl}>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>{lbl}</div>
            <div style={{ fontSize: 13, color: col, fontWeight: 700 }}>{currency(val)}</div>
          </div>
        ))}
      </div>
    );
  }
  return (
  <div style={{
    display: 'grid', gridTemplateColumns: COLS, padding: '10px 16px', gap: 8,
    alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderTop: '1px solid rgba(255,255,255,0.1)',
  }}>
    <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Total do bloco</span>
    <span />
    <span />
    <span style={{ fontSize: 13, color: '#e8f0fb', fontWeight: 700 }}>{currency(sumField(rows, 'total'))}</span>
    <span style={{ fontSize: 13, color: '#4ade80', fontWeight: 700 }}>{currency(sumField(rows, 'customer_paid_amount'))}</span>
    <span style={{ fontSize: 13, color: color || '#f87171', fontWeight: 700 }}>{currency(sumField(rows, 'remaining'))}</span>
    <span />
    <span />
  </div>
  );
};

const COLS = '1fr 1.4fr 1fr 0.8fr 1fr 1fr 90px 90px 56px';
const HEADERS = ['OR', 'Cliente', 'Vendedor', 'Total', 'Pago', 'Restante', 'Próx. serv.', 'Últ. serv.', ''];

const fmtDateTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('pt-BR') + ' ' + dt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const SaleRow = ({ row }) => {
  const history = useHistory();
  const narrow = useIsNarrow();
  const days = row.days_until_service !== null ? parseInt(row.days_until_service) : null;

  // Cobrança só disponível ANTES do primeiro serviço (até 0 dias antes).
  const _today = new Date(); _today.setHours(0, 0, 0, 0);
  const firstStarted = row.first_service_date
    ? new Date(String(row.first_service_date).slice(0, 10) + 'T00:00:00') < _today
    : false;

  const [modal, setModal]     = useState(false);
  const [sending, setSending] = useState(false);
  const [hist, setHist]       = useState([]);
  const [lastAt, setLastAt]   = useState(row.last_reminder_at || null);

  const openModal = (e) => {
    e.stopPropagation();
    setModal(true);
    apiClient.httpInstance.get(`/payments-overview/${row.id}/reminders`)
      .then(res => setHist(res.data || []))
      .catch(() => setHist([]));
  };

  const send = () => {
    setSending(true);
    apiClient.httpInstance.post(`/payments-overview/${row.id}/reminder`)
      .then(res => {
        setSending(false);
        setLastAt(res.data?.reminder?.sent_at || new Date().toISOString());
        apiClient.httpInstance.get(`/payments-overview/${row.id}/reminders`).then(r => setHist(r.data || [])).catch(() => {});
        alert('Cobrança enviada no WhatsApp.');
        setModal(false);
      })
      .catch(err => {
        setSending(false);
        alert(err?.response?.data?.message || 'Falha ao enviar o WhatsApp.');
      });
  };

  // ── Modal de cobrança (compartilhado entre desktop e mobile) ──────────────
  const cobrancaModal = modal && (
    <div
      onClick={(e) => { e.stopPropagation(); setModal(false); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 12 }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#172B4D', marginBottom: 4 }}>Cobrança por WhatsApp</div>
        <div style={{ fontSize: 13, color: '#5b6b7f', marginBottom: 14 }}>
          {row.customer_name} · {row.references} · restante {currency(row.remaining)}
        </div>
        {lastAt && (
          <div style={{ fontSize: 13, color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
            Já foi enviada uma cobrança em <b>{fmtDateTime(lastAt)}</b>. Tem certeza que deseja enviar novamente?
          </div>
        )}
        {hist.length > 0 && (
          <div style={{ maxHeight: 140, overflowY: 'auto', marginBottom: 14, border: '1px solid #e2e8f0', borderRadius: 6 }}>
            {hist.map(h => (
              <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#475569', padding: '6px 10px', borderBottom: '1px solid #f1f5f9' }}>
                <span>{fmtDateTime(h.sent_at)}{h.user_name ? ' · ' + h.user_name : ''}</span>
                <span style={{ color: h.status === 'sent' ? '#16a34a' : '#dc2626' }}>{h.status === 'sent' ? 'enviado' : 'falhou'}</span>
              </div>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={() => setModal(false)} style={{ padding: '10px 16px', borderRadius: 6, border: '1px solid #cbd5e0', background: '#fff', color: '#172B4D', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={send} disabled={sending} style={{ padding: '10px 16px', borderRadius: 6, border: 'none', background: '#25D366', color: '#fff', cursor: sending ? 'not-allowed' : 'pointer', opacity: sending ? 0.6 : 1 }}>
            {sending ? 'Enviando...' : (lastAt ? 'Reenviar' : 'Enviar')}
          </button>
        </div>
      </div>
    </div>
  );

  // ── CARD (celular / tablet em pé) ─────────────────────────────────────────
  // Uso principal aqui é VER a informação e ENVIAR a cobrança, então o valor
  // restante fica em destaque e o botão de WhatsApp sempre visível. (2026-08)
  if (narrow) {
    return (
      <div
        onClick={() => history.push(`/admin/reservas/detalhe/${row.id}`)}
        style={{ padding: '12px 14px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={{ fontSize: 11, color: '#94a3b8', fontFamily: 'monospace' }}>{row.references}</span>
          {days !== null && (
            <span style={{ fontSize: 11, color: '#60a5fa', fontWeight: 700, whiteSpace: 'nowrap' }}>
              <Icon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 2 }}>event</Icon>{days}d
            </span>
          )}
        </div>

        <div style={{ fontSize: 15, color: '#e8f0fb', fontWeight: 600, margin: '3px 0 8px' }}>{row.customer_name}</div>

        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>Restante</div>
            <div style={{ fontSize: 20, color: '#f87171', fontWeight: 700, lineHeight: 1.1 }}>{currency(row.remaining)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
              Total {currency(row.total)} · Pago <span style={{ color: '#4ade80' }}>{currency(row.customer_paid_amount)}</span>
            </div>
          </div>

          {firstStarted ? (
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#64748b' }}>
              <Icon style={{ fontSize: 16, color: '#475569' }}>block</Icon> serviço iniciado
            </span>
          ) : (
            <button
              onClick={openModal}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, border: 'none', cursor: 'pointer',
                background: lastAt ? 'rgba(74,222,128,0.15)' : '#25D366',
                color: lastAt ? '#4ade80' : '#fff',
                borderRadius: 8, padding: '9px 14px', fontSize: 13, fontWeight: 700,
              }}
            >
              <Icon style={{ fontSize: 17 }}>{lastAt ? 'mark_chat_read' : 'chat'}</Icon>
              {lastAt ? 'Reenviar' : 'Cobrar'}
            </button>
          )}
        </div>

        <div style={{ fontSize: 11, color: '#64748b', marginTop: 8 }}>
          {row.seller_name}
          {row.last_service_date && <> · últ. serv. {fmtDate(row.last_service_date)}</>}
          {lastAt && <> · cobrado {fmtDateTime(lastAt)}</>}
        </div>

        {cobrancaModal}
      </div>
    );
  }

  return (
    <div
      onClick={() => history.push(`/admin/reservas/detalhe/${row.id}`)}
      style={{
        display: 'grid', gridTemplateColumns: COLS,
        alignItems: 'center', padding: '10px 16px',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
        cursor: 'pointer', gap: 8,
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
    >
      <span style={{ fontSize: 12, color: '#e8f0fb', fontFamily: 'monospace' }}>{row.references}</span>
      <span style={{ fontSize: 13, color: '#e8f0fb' }}>{row.customer_name}</span>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>{row.seller_name}</span>
      <span style={{ fontSize: 13, color: '#e8f0fb' }}>{currency(row.total)}</span>
      <span style={{ fontSize: 13, color: '#4ade80' }}>{currency(row.customer_paid_amount)}</span>
      <span style={{ fontSize: 13, color: '#f87171', fontWeight: 600 }}>{currency(row.remaining)}</span>
      <span style={{ fontSize: 12, color: '#60a5fa', fontWeight: 600 }}>
        {days !== null
          ? <><Icon style={{ fontSize: 11, verticalAlign: 'middle', marginRight: 2 }}>event</Icon>{days}d</>
          : fmtDate(null)
        }
      </span>
      <span style={{ fontSize: 12, color: '#94a3b8' }}>{fmtDate(row.last_service_date)}</span>

      {firstStarted ? (
        <span title="Serviço já iniciado — cobrança indisponível" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon style={{ fontSize: 18, color: '#475569' }}>block</Icon>
        </span>
      ) : (
        <span
          onClick={openModal}
          title={lastAt ? `Cobrança enviada em ${fmtDateTime(lastAt)} — clique p/ histórico/reenviar` : 'Enviar cobrança no WhatsApp'}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
        >
          <Icon style={{ fontSize: 20, color: lastAt ? '#4ade80' : '#25D366' }}>
            {lastAt ? 'mark_chat_read' : 'chat'}
          </Icon>
        </span>
      )}

      {cobrancaModal}
    </div>
  );
};

const SectionHeader = ({ band, count, open, onToggle }) => (
  <div
    onClick={onToggle}
    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: band.bg, cursor: 'pointer' }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Icon style={{ color: band.color, fontSize: 18 }}>{band.icon}</Icon>
      <span style={{ color: band.color, fontWeight: 700, fontSize: 14 }}>{band.label}</span>
      <span style={{ color: '#94a3b8', fontSize: 12 }}>— {band.desc}</span>
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ background: band.color, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{count}</span>
      <Icon style={{ color: band.color, fontSize: 18 }}>{open ? 'expand_less' : 'expand_more'}</Icon>
    </div>
  </div>
);

const TableHead = () => {
  const narrow = useIsNarrow();
  if (narrow) return null; // no card não há cabeçalho de colunas
  return (
    <div style={{ display: 'grid', gridTemplateColumns: COLS, padding: '6px 16px', background: 'rgba(255,255,255,0.03)', gap: 8 }}>
      {HEADERS.map(h => (
        <span key={h} style={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase' }}>{h}</span>
      ))}
    </div>
  );
};

const BandSection = ({ band, rows }) => {
  const [open, setOpen] = useState(true);
  if (!rows.length) return null;
  return (
    <div style={{ marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: `1px solid ${band.color}33` }}>
      <SectionHeader band={band} count={rows.length} open={open} onToggle={() => setOpen(o => !o)} />
      {open && (<><TableHead />{rows.map(row => <SaleRow key={row.id} row={row} />)}<TableFoot rows={rows} color={band.color} /></>)}
    </div>
  );
};

const Index = (props) => {
  document.title = `Pagamentos - ${process.env.REACT_APP_NAME}`;
  const narrow = useIsNarrow();

  const [loading, setLoading]     = useState(true);
  const [partials, setPartials]   = useState([]);
  const [paid, setPaid]           = useState([]);
  const [showPaid, setShowPaid]           = useState(false);
  const [showConcluded, setShowConcluded] = useState(false);
  const [showLost, setShowLost]           = useState(true);
  const [sellers, setSellers]     = useState([]);

  const [q, setQ]                   = useState('');
  const [search, setSearch]         = useState('');
  const [sellerFilter, setSeller]   = useState('');
  const [bandFilter, setBandFilter] = useState('');

  useEffect(() => { props.setShowMenu(false); }, []);

  useEffect(() => {
    apiClient.httpInstance.get('/users?seller=true&status=true&pageSize=500')
      .then(res => {
        const data = res.data?.data ?? res.data ?? [];
        setSellers([
          { value: '', label: 'Todos os consultores' },
          ...(Array.isArray(data) ? data : data.data ?? []).map(u => ({ value: u.name, label: u.name })),
        ]);
      }).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    apiClient.httpInstance.get('/payments-overview', { params: { q: search || undefined } })
      .then(res => {
        setPartials(res.data.partials || []);
        setPaid(res.data.paid?.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [search]);

  // Classifica cada partial conforme a régua — sem "Sem serviço ativo"
  const activePartials    = partials.filter(r => r.days_until_service !== null);
  const concludedPartials = partials.filter(r => r.days_until_service === null && classifyPostService(r) === 'concluded');
  const lostPartials      = partials.filter(r => r.days_until_service === null && classifyPostService(r) === 'lost');

  const applyCommon = (list, key) => list.filter(r => {
    if (sellerFilter && r.seller_name !== sellerFilter) return false;
    if (bandFilter && bandFilter !== key && bandFilter !== '') return false;
    return true;
  });

  const filteredActive    = activePartials.filter(r => {
    if (sellerFilter && r.seller_name !== sellerFilter) return false;
    if (bandFilter && !['', 'critical','urgent','warning','normal','low'].includes(bandFilter)) return false;
    if (bandFilter && bandFilter !== '') {
      const days = r.days_until_service !== null ? parseInt(r.days_until_service) : null;
      return getBand(days).key === bandFilter;
    }
    return true;
  });
  const filteredConcluded = applyCommon(concludedPartials, 'concluded');
  const filteredLost      = applyCommon(lostPartials,      'lost');

  const bandedPartials = URGENCY_BANDS.map(band => ({
    band,
    rows: filteredActive.filter(r => {
      const days = r.days_until_service !== null ? parseInt(r.days_until_service) : null;
      return getBand(days).key === band.key;
    }),
  }));

  const BAND_BTNS = [
    { key: '',          label: 'Todos' },
    { key: 'critical',  label: '≤ 3 dias' },
    { key: 'urgent',    label: '≤ 7 dias' },
    { key: 'warning',   label: '≤ 30 dias' },
    { key: 'normal',    label: '≤ 60 dias' },
    { key: 'low',       label: '60+ dias' },
    { key: 'concluded', label: 'Alerta', color: CONCLUDED_BAND.color },
    { key: 'lost',      label: 'Perdidos',   color: LOST_BAND.color },
  ];

  return (
    <div className="px-4 md:px-7 pb-10">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 8, marginBottom: 16, color: props.theme.color }}>
        <div style={{ display: 'flex', alignItems: 'center', fontSize: 22, fontWeight: 600 }}>
          <Icon className="mr-2">payments</Icon>
          <span>Pagamentos</span>
        </div>
      </div>

      {/* Filtros — no celular busca e select ocupam a linha inteira */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <TextField
          className="inputRounded"
          label="Buscar cliente ou OR"
          width={narrow ? '100%' : '240px'}
          variant="outlined"
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && setSearch(q)}
          InputProps={{ style: { color: '#e8f0fb', background: '#172B4D' } }}
          InputLabelProps={{ style: { color: '#8ba5c4' } }}
        />
        <div style={{ borderRadius: 20, border: '1px solid #1e3a5a', padding: '5px 0', minWidth: 220, width: narrow ? '100%' : undefined }}>
          <SelectSearch
            styles={darkSelectStyles}
            options={sellers}
            value={sellers.find(s => s.value === sellerFilter) || sellers[0]}
            onChange={ev => setSeller(ev.value)}
            placeholder="Todos os consultores"
          />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {BAND_BTNS.map(b => {
            const band = URGENCY_BANDS.find(ub => ub.key === b.key);
            const color = b.color || (band ? band.color : '#1e3a5a');
            const active = bandFilter === b.key;
            return (
              <button key={b.key} onClick={() => setBandFilter(b.key)} style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${color}`,
                background: active ? color : 'transparent',
                color: active ? '#fff' : color,
              }}>{b.label}</button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <CircularProgress style={{ color: props.theme.color }} />
        </div>
      ) : (
        <>
          {filteredActive.length === 0 && !bandFilter ? (
            <div style={{ textAlign: 'center', color: '#94a3b8', padding: '48px 0' }}>
              <Icon style={{ fontSize: 40 }}>check_circle</Icon>
              <p style={{ marginTop: 8 }}>Nenhum pagamento parcial pendente.</p>
            </div>
          ) : (
            <>
              {bandedPartials.map(({ band, rows }) => <BandSection key={band.key} band={band} rows={rows} />)}
            </>
          )}

          {/* Seção Pagos */}
          {(!bandFilter || bandFilter === '') && (
            <div style={{ marginTop: 32 }}>
              <div
                onClick={() => setShowPaid(o => !o)}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: 'rgba(74,222,128,0.07)', borderRadius: 10, cursor: 'pointer', border: '1px solid rgba(74,222,128,0.2)' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon style={{ color: '#4ade80', fontSize: 18 }}>check_circle</Icon>
                  <span style={{ color: '#4ade80', fontWeight: 700, fontSize: 14 }}>Pagos</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ background: '#4ade80', color: '#000', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{paid.length}</span>
                  <Icon style={{ color: '#4ade80' }}>{showPaid ? 'expand_less' : 'expand_more'}</Icon>
                </div>
              </div>
              {showPaid && paid.length > 0 && (
                <div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(74,222,128,0.15)' }}>
                  <TableHead />
                  {paid.map(row => <SaleRow key={row.id} row={row} />)}
                  <TableFoot rows={paid} color="#4ade80" />
                </div>
              )}
            </div>
          )}

          {/* Seção Concluídos (4–29 dias sem pagamento após serviço) */}
          {filteredConcluded.length > 0 && (!bandFilter || bandFilter === 'concluded') && (
            <div style={{ marginTop: 16 }}>
              <div onClick={() => setShowConcluded(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: CONCLUDED_BAND.bg, borderRadius: 10, cursor: 'pointer', border: `1px solid ${CONCLUDED_BAND.color}33` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon style={{ color: CONCLUDED_BAND.color, fontSize: 18 }}>{CONCLUDED_BAND.icon}</Icon>
                  <span style={{ color: CONCLUDED_BAND.color, fontWeight: 700, fontSize: 14 }}>{CONCLUDED_BAND.label}</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>— {CONCLUDED_BAND.desc}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ background: CONCLUDED_BAND.color, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{filteredConcluded.length}</span>
                  <Icon style={{ color: CONCLUDED_BAND.color, fontSize: 18 }}>{showConcluded ? 'expand_less' : 'expand_more'}</Icon>
                </div>
              </div>
              {showConcluded && (<div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', border: `1px solid ${CONCLUDED_BAND.color}22` }}><TableHead />{filteredConcluded.map(row => <SaleRow key={row.id} row={row} />)}<TableFoot rows={filteredConcluded} color={CONCLUDED_BAND.color} /></div>)}
            </div>
          )}

          {/* Seção Perdidos (31+ dias sem pagamento após serviço) */}
          {filteredLost.length > 0 && (!bandFilter || bandFilter === 'lost') && (
            <div style={{ marginTop: 16 }}>
              <div onClick={() => setShowLost(o => !o)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: LOST_BAND.bg, borderRadius: 10, cursor: 'pointer', border: `1px solid ${LOST_BAND.color}33` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Icon style={{ color: LOST_BAND.color, fontSize: 18 }}>{LOST_BAND.icon}</Icon>
                  <span style={{ color: LOST_BAND.color, fontWeight: 700, fontSize: 14 }}>{LOST_BAND.label}</span>
                  <span style={{ color: '#94a3b8', fontSize: 12 }}>— {LOST_BAND.desc}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ background: LOST_BAND.color, color: '#fff', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>{filteredLost.length}</span>
                  <Icon style={{ color: LOST_BAND.color, fontSize: 18 }}>{showLost ? 'expand_less' : 'expand_more'}</Icon>
                </div>
              </div>
              {showLost && (<div style={{ marginTop: 8, borderRadius: 10, overflow: 'hidden', border: `1px solid ${LOST_BAND.color}22` }}><TableHead />{filteredLost.map(row => <SaleRow key={row.id} row={row} />)}<TableFoot rows={filteredLost} color={LOST_BAND.color} /></div>)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Index;
